import React, { useState } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { scrollToTop } from "../../lib/helpers";
import whitepaperPdf from "../../lib/whitepaperPdf";
import { LEGAL_PATHS } from "../../legal/legalPaths";
import {
  SITE_DASHBOARD_PATH,
  SITE_MARKETPLACE_PATH,
  SITE_NAV_ROUTES,
  SITE_OSHAR_REAL_ESTATE_PATH,
  SITE_PUBLIC_LIBRARY_PATH,
  SITE_SUPPORT_PATH,
  SITE_WORLD_PATH,
} from "../../constants/siteNavRoutes";
import { postNewsletterSubscribe } from "../../services/newsletterSubscribeApi";
import { trackNewsletterSignup, trackSupportClick } from "../../utils/analytics";
import "./site-shell.css";

const footerSocials = [
  { href: "https://www.linkedin.com/in/treydora-productions-inc-1055b5233/", icon: "fab fa-linkedin", label: "LinkedIn" },
  { href: "https://www.reddit.com/user/treydoraproductions", icon: "fab fa-reddit", label: "Reddit" },
  { href: "https://www.pinterest.com/treydoraproductionsinc/_saved/", icon: "fab fa-pinterest", label: "Pinterest" },
  { href: "https://t.me/World_Of_Treydora", icon: "fab fa-telegram", label: "Telegram" },
  { href: "https://twitter.com/TreydoraP_inc", icon: "fab fa-twitter", label: "X" },
  { href: "https://www.facebook.com/Treydora-Productions-Inc-102535382383944", icon: "fab fa-facebook-f", label: "Facebook" },
  { href: "https://www.instagram.com/treydoraproductions/", icon: "fab fa-instagram", label: "Instagram" },
  { href: "https://www.youtube.com/@WorldofTreydora", icon: "fab fa-youtube", label: "YouTube" },
  { href: "https://discord.gg/D7BJQSqAAE", icon: "fab fa-discord", label: "Discord" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function SiteFooter() {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState("");
  const [formSuccess, setFormSuccess] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");
    setFormSuccess("");
    const trimmed = email.trim();
    if (!EMAIL_RE.test(trimmed)) {
      setFormError("Enter a valid email address.");
      return;
    }
    setSubmitting(true);
    try {
      const result = await postNewsletterSubscribe({
        email: trimmed,
        source: "treydora-footer",
        page: typeof window !== "undefined" ? window.location.pathname || "home" : "home",
        segments: ["general"],
      });
      const successMessage =
        typeof result?.message === "string" && result.message.trim()
          ? result.message.trim()
          : "You're on the list. Watch for Treydora updates.";
      setFormSuccess(successMessage);
      toast.success(successMessage, {
        position: "bottom-right",
        autoClose: 5000,
        theme: "dark",
      });
      trackNewsletterSignup({
        cta_id: "footer_newsletter_join",
        section: "site_footer",
      });
      setEmail("");
    } catch (err) {
      const errorMessage =
        typeof err?.message === "string" && err.message.trim()
          ? err.message.trim()
          : "Subscription failed. Please try again.";
      if (process.env.NODE_ENV !== "production") {
        console.error("[newsletter] footer subscribe error", err);
      }
      toast.error(errorMessage, {
        position: "bottom-right",
        autoClose: 5000,
        theme: "dark",
      });
      setFormError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer className="td-chrome-footer">
      <div className="td-chrome-footer__inner">
        <div className="td-chrome-footer__grid">
          <div className="td-chrome-footer__col">
            <h3 className="td-chrome-footer__title">Treydora</h3>
            <p className="td-chrome-footer__blurb">
              Oshar VR, Treydorex, and the gateway economy — a cinematic front for player-driven worlds and on-chain
              participation.
            </p>
            <div className="td-chrome-footer__socials">
              {footerSocials.map((s) => (
                <a key={s.href} href={s.href} target="_blank" rel="noreferrer noopener" aria-label={s.label}>
                  <i className={s.icon} />
                </a>
              ))}
            </div>
          </div>
          <div className="td-chrome-footer__col">
            <h3 className="td-chrome-footer__title">Discover</h3>
            <ul className="td-chrome-footer__links">
              <li>
                <Link to={SITE_WORLD_PATH}>World of Treydora</Link>
              </li>
              <li>
                <Link to={SITE_OSHAR_REAL_ESTATE_PATH}>Oshar real estate</Link>
              </li>
              <li>
                <Link to={SITE_WORLD_PATH}>The game</Link>
              </li>
              <li>
                <HashLink smooth to={SITE_NAV_ROUTES.TOKEN_SALE}>
                  Treydorex
                </HashLink>
              </li>
              <li>
                <Link to={SITE_PUBLIC_LIBRARY_PATH}>Library</Link>
              </li>
              <li>
                <Link to={SITE_MARKETPLACE_PATH}>Marketplace</Link>
              </li>
              <li>
                <Link to={SITE_DASHBOARD_PATH}>Gateway</Link>
              </li>
              <li>
                <Link
                  to={SITE_SUPPORT_PATH}
                  onClick={() =>
                    trackSupportClick({
                      cta_id: "footer_support_link",
                      section: "site_footer",
                      destination_type: "internal",
                    })
                  }
                >
                  Support
                </Link>
              </li>
            </ul>
          </div>
          <div className="td-chrome-footer__col">
            <h3 className="td-chrome-footer__title">Legal vault</h3>
            <ul className="td-chrome-footer__links td-chrome-footer__links--legal">
              <li>
                <Link to={LEGAL_PATHS.center}>Legal Center</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.terms}>Terms of Use</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.privacy}>Privacy Policy</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.digitalAssetRisk}>Digital Asset Risk Disclosure</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.nftComicMerchLicense}>NFT Comic Merchandiser Terms</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.vrRealEstateTerms}>VR Real Estate NFT Terms</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.marketplaceSellerTerms}>Marketplace Seller Terms</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.refundPolicy}>Refund Policy</Link>
              </li>
              <li>
                <Link to={LEGAL_PATHS.contentStandards}>Content Standards</Link>
              </li>
            </ul>
          </div>
          <div className="td-chrome-footer__col">
            <h3 className="td-chrome-footer__title">Contact</h3>
            <ul className="td-chrome-footer__links">
              <li>
                <a href={whitepaperPdf} download="treydorawhitepaper">
                  Whitepaper
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@treydora.com"
                  onClick={() =>
                    trackSupportClick({
                      cta_id: "footer_contact_email",
                      section: "site_footer",
                      destination_type: "email",
                    })
                  }
                >
                  info@treydora.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="td-chrome-footer__newsletter">
          <div>
            <h4 className="td-chrome-footer__title">Signal list</h4>
            <p>Drop your email for drops, mint windows, and Oshar district releases.</p>
          </div>
          <div className="td-chrome-footer__newsletter-glass">
            <form className="td-chrome-footer__form" onSubmit={handleSubmit} noValidate>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                aria-label="Email for newsletter"
                disabled={submitting}
                autoComplete="email"
              />
              <button type="submit" disabled={submitting} aria-busy={submitting}>
                {submitting ? "Sending…" : "Join"}
              </button>
            </form>
            <p className="td-chrome-footer__consent">
              By subscribing, you agree to receive Treydora updates. You can unsubscribe anytime.
            </p>
            {formSuccess ? (
              <p className="td-chrome-footer__form-success" role="status">
                {formSuccess}
              </p>
            ) : null}
            {formError ? (
              <p className="td-chrome-footer__form-error" role="alert">
                {formError}
              </p>
            ) : null}
          </div>
        </div>

        <div className="td-chrome-footer__bottom">
          <span>© {new Date().getFullYear()} Treydora Productions, Inc.</span>
          <button type="button" className="td-chrome-footer__to-top" onClick={scrollToTop}>
            <i className="fas fa-arrow-up" aria-hidden /> Top
          </button>
        </div>
      </div>
      <ToastContainer />
    </footer>
  );
}
